import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/common/dialog"
import { Copy, Mail, Maximize, Monitor, Pen, X } from "lucide-react"
import { Button } from "@components/common/button"
import VideoPlayer from "@components/files/video-player"
import AudioPlayer from "@components/files/audio-player"
import { Link } from "react-router-dom"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import useTranslator from "@hooks/use-translator"
import { ReactNode, useEffect, useRef, useState } from "react"
import DocumentViewer from "@components/files/document-preview"
import { DictType } from "@pma/types"
import { cn, formatDate, formatDate_he } from "@pma/utils/helpers"
import PmsLoader from "@components/common/loader"
import TextFileViewer from "@components/files/text-file-viewer"
import ShareFileOnWhatsApp from "@components/files/share-file-on-whatsapp"
import DownloadFile from "@components/files/download-file"
import DeleteFile from "@components/dashboard/delete-file"
import PrintFile from "@components/files/print-file"
import ExcelFileViewer from "@components/files/excel-file-viewer"
import ShareFilesFoldersViaEmail from "../files/share-files-folders-via-email"

interface Props {
  file: DictType
  children?: ReactNode
  isSelectionOn?: boolean
  onClick?: () => void | undefined
}
export function FilePreview({ file, children, isSelectionOn, onClick }: Props) {
  const imgRef = useRef<HTMLImageElement>(null)
  const { translate, locale } = useTranslator()

  const [isOpen, setIsOpen] = useState(false)
  const [isMaximize, setIsMaximize] = useState(true)
  const [annotating, setAnnotating] = useState(false)
  const [showOption, setShowOption] = useState(false)
  const [isImgLoading, setIsImgLoading] = useState(true)
  const [originalHeight, setOriginalHeight] = useState<number>(300)
  const [selectedFiles, setSelectedFiles] = useState<DictType[]>([])
  const [customHeight, setCustomHeight] = useState<number | "auto">("auto")
  const [shareViaEmailModal, setShareViaEmailModal] = useState<boolean>(false)
  const [renderPdfInAnnotationMode, setRenderPdfInAnnotationMode] = useState(false)

  const videoExtensions = ["mov", "mp4", "webm"]
  const audioExtensions = ["mp3"]
  const textFilesExtensions = ["txt", "docx", "doc", "csv", "pptx"]
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"]
  const pdfExtensions = ["pdf", "PDF"]
  const excelExtensions = ["xlsx", "xls"]
  const unsupportedIframesFilesExtensions = ["ifc", "odd"]
  const isVideo = videoExtensions.includes(file?.type)
  const isAudio = audioExtensions.includes(file?.type)
  const isTextFile = textFilesExtensions.includes(file?.type)
  const isImage = imageExtensions.includes(file?.type)
  const isExcel = excelExtensions.includes(file?.type)
  const isPDF = pdfExtensions.includes(file?.type)
  const isUnsupportedIframeExtension = unsupportedIframesFilesExtensions.includes(file?.type)
  const isUnsupportedPreviewExtension =
    !isVideo &&
    !isAudio &&
    !isTextFile &&
    !isImage &&
    !isExcel &&
    !isPDF &&
    !isUnsupportedIframeExtension

  const [documentLoading, setDocumentLoading] = useState(!isVideo && !isAudio && !isImage && !isExcel && !isTextFile)

  const isHebrew = locale === "he"

  const handleClick = () => {
    if (!isSelectionOn) setIsOpen(true)
    else onClick && onClick()
  }

  useEffect(() => {
    if (isPDF) {
      fetch(file?.file, { method: "HEAD" }).then((response) => {
        for (let [key, value] of response.headers.entries()) {
          if (key == "content-type" && value != "application/pdf") {
            setRenderPdfInAnnotationMode(true)
          }
        }
      })
    }
  }, [file])

  useEffect(() => {
    if (renderPdfInAnnotationMode) setAnnotating(true)
  }, [renderPdfInAnnotationMode])

  // Auto-enable annotation mode if image has existing annotations
  useEffect(() => {
    if (isOpen && isImage && file?.annotations?.length) {
      setAnnotating(true)
      setShowOption(true)
    }
  }, [isOpen, file, isImage])

  const handleMaximize = () => setIsMaximize(!isMaximize)

  const handleImageLoad = () => {
    setIsImgLoading(false)
    if (imgRef.current) {
      const naturalHeight = imgRef.current.naturalHeight
      setOriginalHeight(naturalHeight)
    }
  }

  const increaseHeight = () => {
    if (annotating && (isImage || isPDF)) {
      // For annotation mode, work with viewport height
      setCustomHeight((prev) => {
        if (prev === "auto") {
          return 500 // Start with a reasonable height in pixels
        }
        return Math.min(prev + 50, 1000)
      })
    } else {
      // For normal image view, use original logic
      setCustomHeight((prev) => (prev === "auto" ? originalHeight + 50 : Math.min(prev + 50, 1000)))
    }
  }

  const decreaseHeight = () => {
    if (annotating && (isImage || isPDF)) {
      // For annotation mode, work with viewport height
      setCustomHeight((prev) => {
        if (prev === "auto") {
          return Math.max(400, 300) // Start with a reasonable minimum
        }
        return Math.max(prev - 50, 300)
      })
    } else {
      // For normal image view, use original logic
      setCustomHeight((prev) =>
        prev === "auto" ? Math.max(originalHeight - 50, 100) : Math.max(prev - 50, 100),
      )
    }
  }

  const resetHeight = () => {
    if (annotating && (isImage || isPDF)) {
      // For annotation mode, reset to a standard height (like 90vh equivalent)
      setCustomHeight(650) // Approximately 90vh on most screens
    } else {
      // For normal image view, reset to auto
      setCustomHeight("auto")
    }
  }

  const closeDialog = () => {
    setIsOpen(false)
    setIsMaximize(true)
    setAnnotating(false)
    setRenderPdfInAnnotationMode(false)
    setIsImgLoading(true)
    setCustomHeight("auto")
    setDocumentLoading(!isVideo && !isAudio && !isImage && !isExcel && !isTextFile)
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={closeDialog}>
        {children ? (
          <div className='flex cursor-pointer flex-col' onClick={handleClick}>
            <div className='w-full'>{children}</div>
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger>
              <div className='w-full' onClick={handleClick}>
                <Button variant={"ghost"} className='text-xs' size={"icon"}>
                  <Monitor className='text-primary' size={16} />
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent className='bg-accent text-black'>
              {translate("Preview")}/{translate("Annotate")}
            </TooltipContent>
          </Tooltip>
        )}
        <DialogContent
          key={isMaximize ? "max" : "min"}
          className={`flex flex-col gap-2 overflow-auto px-0 pb-1 transition-all ease-in-out ${isMaximize ? "h-full max-w-full" : "h-[90vh] w-full md:max-w-[90vw] lg:max-w-[80vw]"}`}
        >
          <DialogHeader className='sticky top-0 z-10 flex h-fit w-full items-center gap-1 py-2 shadow-md'>
            <div className='flex justify-end'>
              {isMaximize ? (
                <Tooltip>
                  <TooltipTrigger
                    onFocusCapture={(e) => {
                      // To Stop tooltip open by default
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    className={cn("absolute top-[12px]", isHebrew ? "left-10" : "right-10")}
                  >
                    <Copy
                      className={`absolute top-[-20px] cursor-pointer ${isHebrew ? "left-[5px]" : "right-[5px]"}`}
                      size={16}
                      onClick={handleMaximize}
                    />
                  </TooltipTrigger>
                  <TooltipContent side='bottom'>
                    <h4>{translate("Minimize")}</h4>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger
                    onFocusCapture={(e) => {
                      // To Stop tooltip open by default
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    className={cn("absolute top-[12px]", isHebrew ? "left-10" : "right-10")}
                  >
                    <Maximize
                      className={`absolute top-[-20px] cursor-pointer ${isHebrew ? "left-[5px]" : "right-[5px]"}`}
                      size={16}
                      onClick={handleMaximize}
                    />
                  </TooltipTrigger>
                  <TooltipContent side='bottom'>
                    <h4>{translate("Maximize")}</h4>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <DialogTitle className='w-fit capitalize'>{file?.name}</DialogTitle>
            <div className='flex items-center gap-1 text-sm'>
              <span className='text-xs'>{translate("Uploaded")}:</span>
              <span className='text-xs'>
                {isHebrew ? formatDate_he(file?.created_at) : formatDate(file?.created_at)}
              </span>
              <span className='text-xs'>{translate("by")}</span>
              <Link to={USERS_PAGE_ROUTE + `/${file?.user?.id}`}>
                <Button variant={"link"} className='h-fit p-0'>
                  <h4>{file?.user?.name}</h4>
                </Button>
              </Link>
            </div>

            <div className='flex'>
              {!isVideo && !isAudio && !isTextFile && !isExcel && (
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      size={"icon"}
                      className='h-6 w-7'
                      variant={annotating ? "destructive" : "ghost"}
                      aria-label='Download'
                      onClick={() => {
                        const newAnnotatingState = !annotating
                        setAnnotating(newAnnotatingState)
                        if (!renderPdfInAnnotationMode) {
                          setShowOption(newAnnotatingState)
                        }
                      }}
                    >
                      {showOption && isPDF ? (
                        renderPdfInAnnotationMode
                      ) : showOption ? (
                        <X size={14} />
                      ) : (
                        <Pen size={14} />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{translate("Annotate")}</TooltipContent>
                </Tooltip>
              )}
              <DownloadFile file={file} />
              <PrintFile file={file} />
              <ShareFileOnWhatsApp file={file} />
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    size={"icon"}
                    className='h-6 w-7'
                    variant={"ghost"}
                    aria-label={translate("Share on Mail")}
                    onClick={() => {
                      setSelectedFiles([file])
                      setShareViaEmailModal(true)
                    }}
                  >
                    <Mail size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{translate("Share on Mail")}</TooltipContent>
              </Tooltip>
              <DeleteFile file={file} />
              {(isImage || (isPDF && annotating)) && (
                <div className='flex items-center gap-1'>
                  <Button
                    size={"icon"}
                    className='h-6 w-7'
                    variant={"ghost"}
                    onClick={decreaseHeight}
                    title={translate("Decrease Height")}
                  >
                    <span className='text-sm font-bold'>-</span>
                  </Button>
                  <Button
                    size={"icon"}
                    className='h-6 w-7'
                    variant={"ghost"}
                    onClick={resetHeight}
                    title={translate("Reset Height")}
                  >
                    <span className='text-xs'>{translate("Reset")}</span>
                  </Button>
                  <Button
                    size={"icon"}
                    className='h-6 w-7'
                    variant={"ghost"}
                    onClick={increaseHeight}
                    title={translate("Increase Height")}
                  >
                    <span className='text-sm font-bold'>+</span>
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          <div className='flex-1 overflow-auto bg-gray-300/30 px-2 relative'>
            {documentLoading && annotating && (
              <div className='absolute inset-0 flex items-center justify-center bg-black/80 z-10'>
                <PmsLoader />
              </div>
            )}
            {file && (
              <>
                {isVideo && <VideoPlayer video={file?.file} />}
                {isTextFile && <TextFileViewer file={file} />}
                {isAudio && <AudioPlayer audio={file?.file} />}
                {isExcel && <ExcelFileViewer file={file} />}
                {isImage && !annotating && (
                  <img
                    ref={imgRef}
                    src={file?.file}
                    alt={file?.name}
                    onLoad={handleImageLoad}
                    onError={() => setIsImgLoading(false)}
                    className='m-auto w-auto max-w-full object-contain'
                    style={{ height: customHeight === "auto" ? "auto" : `${customHeight}px` }}
                  />
                )}

                {isUnsupportedPreviewExtension && (
                  <div className='m-auto flex h-full w-[50vw] items-center justify-center'>
                    <div
                      className='z-50 flex h-[40vh] w-full flex-col items-center justify-center overflow-y-auto object-contain p-2'
                      style={{ direction: isHebrew ? "rtl" : "ltr" }}
                    >
                      <p>{translate("This file type is not supported for preview")}.</p>
                      <p className='mb-2'>
                        {translate("You can download file using this button")}.
                      </p>
                      <DownloadFile file={file} showText={true} />
                    </div>
                  </div>
                )}

                {isPDF && !renderPdfInAnnotationMode && !annotating && (
                  <div className='flex h-[90vh] w-[80vw] items-center justify-center'>
                    <iframe
                      src={`${String(file?.file)}#toolbar=0&navpanes=0&scrollbar=0`}
                      title={String(file?.name)}
                      className='z-50 aspect-video h-full w-full overflow-y-auto object-contain'
                    />
                  </div>
                )}

                {!annotating && (
                  <>
                    {(isImage || isPDF) && isImgLoading && (
                      <div className='relative m-auto flex h-[50vh] w-[70vw] items-center justify-center'>
                        {isImage && isImgLoading && (
                          <p className='absolute bottom-0 left-0 right-0 top-0 flex animate-pulse items-center justify-center shadow'>
                            {translate("Please wait")}. {translate("Image is loading")}...
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
                {(annotating || isUnsupportedIframeExtension) && (
                  <>
                    {!isVideo && !isAudio && !isTextFile && !isExcel && (
                      <DocumentViewer
                        setDocumentLoading={setDocumentLoading}
                        file={file}
                        showOption={showOption}
                        customHeight={customHeight}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {shareViaEmailModal && (
        <ShareFilesFoldersViaEmail
          modalOpen={shareViaEmailModal}
          setModalOpen={setShareViaEmailModal}
          selectedFiles={selectedFiles}
          project={{ id: file?.project_id }}
        />
      )}
    </>
  )
}
